/**
 * The dynamic-code hand-off between a listing and the reveal page.
 *
 * Some coupons are issued per visitor: `POST /coupons/click` answers with a
 * `dynamicCode` that belongs to that click and no other. The listing copies it
 * and stashes it here; the reveal page reads it back so both tabs show the same
 * code. Without this the reveal page would ask for its own, and the visitor
 * would have one code on their clipboard and a different one on screen.
 *
 * Key and storage match the Next.js `lib/coupons/dynamicCodeStorage.ts`
 * exactly, because the two front ends have to interoperate during a staged
 * cutover — a visitor can land on a generated listing and a Next.js reveal page
 * in the same session:
 *
 *   key      `dynamicCode_<COUPONID UPPERCASED>`  (normalizeCouponId)
 *   written  to BOTH localStorage and sessionStorage
 *
 * .in uses a bare `dynamicCode_<id>` in sessionStorage only. Following it here
 * would silently miss every code the other app wrote.
 */

/** `normalizeCouponId` from the Next.js useCouponUsesToday hook. */
export function dynamicCodeKey(couponId) {
  return `dynamicCode_${String(couponId ?? '').trim().toUpperCase()}`;
}

export function storeDynamicCode(couponId, code) {
  const value = String(code ?? '').trim();
  if (!value) return;
  const key = dynamicCodeKey(couponId);
  // Both, as the Next.js helper does: sessionStorage survives the tab, and
  // localStorage survives the tab being replaced by window.location.href.
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — the page still works, it just re-fetches */
  }
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* as above */
  }
}

/**
 * Reads the stashed code. `consume` removes it, which is what the reveal page
 * wants: a code left behind would be shown again on a later, unrelated visit.
 */
export function readDynamicCode(couponId, consume) {
  const key = dynamicCodeKey(couponId);
  let value = null;
  try {
    value = sessionStorage.getItem(key) || localStorage.getItem(key);
  } catch {
    return null;
  }
  if (value && consume) {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch {
      /* nothing to do */
    }
  }
  return value || null;
}
